import { useState } from 'react';

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: "How quickly can you buy my land?",
      answer: "We can close in as little as 7 days if you need a fast sale. Our typical timeline is 7-14 days from accepting our offer to closing. If you need more time, we can accommodate that too - we work on your schedule. Unlike traditional sales that take 6-12 months, our cash purchases eliminate financing contingencies, inspections, and other delays."
    },
    {
      question: "Do you really pay cash, or is it financed?",
      answer: "We pay 100% cash at closing. We don't need bank financing, so there's no risk of the deal falling through due to loan denial. When we make an offer, we have the funds ready to close. Your payment is wired directly to your bank account at closing - no waiting for checks to clear."
    },
    {
      question: "Will you buy land with back taxes or liens?",
      answer: "Yes! We regularly purchase land with delinquent property taxes, tax liens, mechanic's liens, and other encumbrances. We'll order a title search to identify all liens, calculate your equity, and make an offer based on the net value. At closing, we pay off all liens directly, and you receive whatever equity remains. This is much better than losing your property to foreclosure."
    },
    {
      question: "What if my land has no utilities or access?",
      answer: "We buy off-grid land regularly. Lack of utilities, dirt road access, or remote locations don't stop us from making offers. We evaluate land based on its potential and our investment criteria, not traditional lending standards. Many of our best purchases have been properties that other buyers considered 'unbuildable' or 'worthless.'"
    },
    {
      question: "How do you determine your offer price?",
      answer: "We research comparable sales in your area, review county records for zoning and restrictions, assess access and utilities, and factor in current market conditions. Our goal is to make a fair offer that reflects true market value while accounting for the speed and convenience of a cash sale. We're transparent about our valuation and happy to explain how we arrived at our offer."
    },
    {
      question: "Do I have to pay realtor commissions or closing costs?",
      answer: "No! We pay all closing costs including title insurance, escrow fees, and transfer taxes. There are no realtor commissions because we buy directly from you. The offer we make is the amount you receive at closing (minus any liens or back taxes that need to be paid off). What you see is what you get."
    },
    {
      question: "What if my land is in probate or part of an estate?",
      answer: "We have extensive experience buying inherited land and properties in probate. We can work with executors, administrators, and multiple heirs to structure a sale that satisfies all parties and court requirements. We'll wait for probate approval if needed and can even provide referrals to probate attorneys if you need legal guidance."
    },
    {
      question: "Can you buy land with title problems?",
      answer: "Yes, we specialize in problem properties. Clouded title, boundary disputes, missing heirs, easement issues, or other title complications don't necessarily prevent a sale. We have relationships with title companies and attorneys who can resolve many title issues. Even if the problem seems insurmountable, contact us - we may have a solution."
    },
    {
      question: "What areas do you buy land in?",
      answer: "We purchase land throughout El Paso County, Texas and Doña Ana County, New Mexico. This includes El Paso, Horizon City, Socorro, Clint, Fabens, Anthony, Chaparral, Sunland Park, and all surrounding areas. We buy both urban infill lots and rural acreage. If your property is in the region, we're interested."
    },
    {
      question: "Is there any obligation when I contact you?",
      answer: "Absolutely not. When you call or contact us, we'll ask some basic questions about your property and provide a free, no-obligation cash offer. You're under no pressure to accept. Take time to think it over, compare it to other options, and make the decision that's right for you. Many sellers contact us just to see what their land is worth."
    },
    {
      question: "What if I owe more than the land is worth?",
      answer: "If your liens and back taxes exceed the property's value, you may have limited options. However, we can sometimes negotiate with lienholders to accept reduced payoffs, or structure creative solutions. Even if you can't walk away with cash, selling voluntarily is better than foreclosure - it protects your credit and eliminates future liability. Contact us to discuss your specific situation."
    },
    {
      question: "How is selling to you different from listing with a realtor?",
      answer: "Speed: We close in 7-14 days vs. 6-12 months. Cost: No commissions or closing costs vs. 6-10% commission plus $2,000-$3,000 in closing costs. Certainty: Guaranteed cash vs. uncertain financing that often falls through. Convenience: We buy as-is vs. requiring improvements and dealing with showings. Hassle: We handle everything vs. months of marketing and negotiations."
    },
    {
      question: "What happens after I accept your offer?",
      answer: "We'll open escrow with a reputable title company, order a title search and survey if needed, prepare all closing documents, and schedule a closing date that works for you. At closing, you'll sign the deed and receive your cash payment via wire transfer. The entire process is handled by professionals, and we guide you through every step."
    },
    {
      question: "Can I sell just part of my land?",
      answer: "Possibly. If you own a large parcel and only want to sell a portion, we can discuss subdividing the property. This requires surveying and potentially platting, which adds time and cost. In some cases, we can purchase the entire parcel and then subdivide it ourselves. Contact us to discuss your specific situation."
    },
    {
      question: "Why should I trust TRex RE LLC?",
      answer: "We've been buying land in El Paso since 2016 and have completed over 200 transactions. We're not wholesalers or middlemen - we're actual end buyers with a proven track record. We work with reputable title companies, maintain proper licensing and insurance, and have dozens of satisfied sellers who can attest to our professionalism. You can verify our business credentials and check our reputation before making any commitment."
    }
  ];

  return (
    <section id="faq" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            Everything you need to know about selling your land for cash to TRex RE LLC.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-gray-50 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <span className="font-bold text-gray-900 pr-8">{faq.question}</span>
                <i className={`ri-arrow-${openIndex === index ? 'up' : 'down'}-s-line text-teal-600 text-2xl flex-shrink-0`}></i>
              </button>
              {openIndex === index && (
                <div className="px-6 pb-5">
                  <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 bg-teal-50 border-l-4 border-teal-600 p-8 rounded-lg">
          <h3 className="text-2xl font-bold mb-4 text-gray-900">Still Have Questions?</h3>
          <p className="text-gray-700 leading-relaxed mb-6">
            We're here to help. Call us at (915) 206-5814 to speak with a land buying specialist who can answer your specific questions and provide a free, no-obligation cash offer for your property.
          </p>
          <a href="tel:+1-915-206-5814" className="inline-flex items-center space-x-2 px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-phone-fill"></i>
            <span>Call (915) 206-5814</span>
          </a>
        </div>
      </div>
    </section>
  );
}
